/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { memo, useMemo, useEffect, useRef, useState } from "react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { convertDate } from "@Services/luxonHelper";
import ComboBoxComponent from "@CubeComponents/form/ComboBoxComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { SaveCurrencyData, DeleteCurrencyData, getSymbolsData } from "@Actions/Currency";
import { showToastNotification } from "@Reducers/toastSlice";
import { useDispatch, useSelector } from "react-redux";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getCurrencyDataUrl } from "@Services/dataManager";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import _ from "lodash";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import { recordStatus } from "@Helpers/constants";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import type { RootState } from "@Reducers/index";

const CurrenciesHeader = memo(function CurrenciesHeader({ isActive }: { isActive: boolean }) {
  return (
    <div className="flex flex-col pb-4">
      <GridPageHeaderLeft
        title={isActive ? "Currencies" : "Archived Currencies"}
        description={
          isActive ? "List of all the currencies in this portal" : "List of all the archived currencies in this portal"
        }
      />
    </div>
  );
});

function Currencies() {
  const { t } = useTranslation();
  const dataGrid = useRef<any>(null);
  const dispatch = useDispatch();

  const symbolsRef = useRef<any>(null);
  const [view, setView] = useState<any>(recordStatus.ACTIVE);
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [symbols, setSymbols] = useState<any[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<any>({});
  const [category, setCategory] = useState<string>("Prefix");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [isDefaultChecked, setIsDefaultChecked] = useState<boolean>(false);
  const [symbolsToAdd, setSymbolsToAdd] = useState<any>([]);
  const [query, setQuery] = useState<any>({
    status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
    params: {
      status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
    },
  });
  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [toolBtnState, setToolBtnState] = useState({
    create: true,
    edit: false,
    delete: false,
    archive: false,
    default: false,
    unarchive: false,
  });

  useEffect(() => {
    document.title = t("Portal Settings | Currencies");
  }, []);

  useEffect(() => {
    setQuery({
      status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
      params: {
        status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
      },
    });
  }, [view]);

  const formatCreatedDate = (data: any, value: any, a: any, b: any, params: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={convertDate(Number(params.createdDate), "dateForTooltip")}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const formatName = (data: any, value: any, a: any, b: any, params: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      params.isDefault ? (
        <div className="flex gap-2 items-center">
          <div>{params.name}</div>
          <div className="rounded-2xl bg-yet_to_start_communication text-white text-xs px-2 py-1">
            <Text>{t("Default")}</Text>
          </div>
        </div>
      ) : (
        <div>{params.name}</div>
      ),
    );
    return container;
  };

  const formatCreatedBy = (data: any, value: any, a: any, b: any, params: any) => {
    const container = document.createElement("div");
    if (params.createdBy && params.createdBy != "0") {
      renderUserTag(container, { id: params.createdBy });
    } else {
      container.textContent = t("System Generated");
    }
    return container;
  };

  const currencyColumnDefinitions = useRef([
    {
      id: "name",
      field: "name",
      name: t("Name"),
      filterType: "Text",
      formatter: formatName,
      _valueGetter: (row: any) => (row.name ? (row.isDefault ? `${row.name} (Default)` : row.name) : ""),
    },
    {
      id: "selectedSymbol",
      field: "selected_currency_symbol.selectedSymbol",
      name: t("Symbol"),
      filterType: "Text",
      _valueGetter: (args: any) => _.get(args, "selected_currency_symbol.selectedSymbol"),
    },
    {
      id: "createdDate",
      field: "createdDate",
      name: t("Created On"),
      formatter: formatCreatedDate,
      filterType: "Date",
      _valueGetter: (row: any) => (row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : ""),
    },
    {
      id: "category",
      field: "category",
      name: t("Prefix/Suffix"),
      filterType: "Text",
    },

    {
      id: "createdByName",
      field: "createdByName",
      name: t("Created by"),
      filterType: "Text",
      formatter: formatCreatedBy,
      _valueGetter: (args) => _.get(args, "createdByName"),
    },
  ]);

  const archiveTemplate = () => (
    <SelectDropDown
      buttonType="dropdownBtnCustom"
      select={handleDropDownSelectionHandler}
      items={currencyDropdownButtonsItems}
      allowButtonPadding={false}
      bgColor="bg-surface_white"
      cssClass="e-caret-hide p-0 border-none"
    >
      <IconComponent name="threedots" width="1rem" height="1rem" />
    </SelectDropDown>
  );

  const toolbarOptions = useMemo(() => {
    return view === recordStatus.ACTIVE
      ? [
          {
            name: t("Add"),
            prefixIcon: "add",
            id: "custom_add",
            disabled: !portalSettingInfo?.currencies?.addCurrencies?.enable || !toolBtnState.create,
          },
          {
            name: t("Edit"),
            prefixIcon: "editProfile",
            id: "custom_edit",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo?.currencies?.editCurrencies?.enable || !toolBtnState.edit,
          },
          {
            name: t("Delete"),
            prefixIcon: "delete",
            id: "custom_delete",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo?.currencies?.deleteCurrencies?.enable || !toolBtnState.delete,
          },
          {
            name: t("Archive"),
            prefixIcon: "archive",
            id: "custom_archive",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.archive || !portalSettingInfo?.currencies?.editCurrencies?.enable,
          },
          {
            name: t("Set As Default"),
            prefixIcon: "setAsDefault",
            id: "set_default",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.default || !portalSettingInfo?.currencies?.editCurrencies?.enable,
          },
          {
            id: "archive_dropdown",
            rightAllign: true,
            template: archiveTemplate,
            disabled: !portalSettingInfo?.currencies?.editCurrencies?.enable,
          },
        ]
      : [
          {
            name: t("Unarchive"),
            prefixIcon: "unarchive",
            id: "custom_unarchive",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.unarchive || !portalSettingInfo?.currencies?.editCurrencies?.enable,
          },
          {
            name: t("Delete"),
            prefixIcon: "delete",
            id: "custom_delete",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo?.currencies?.deleteCurrencies?.enable || !toolBtnState.delete,
          },
          {
            id: "archive_dropdown",
            rightAllign: true,
            template: archiveTemplate,
            disabled: !portalSettingInfo?.currencies?.editCurrencies?.enable,
          },
        ];
  }, [view, toolBtnState, portalSettingInfo]);

  const toolbarClickHandler = (args: any) => {
    if (args === "custom_add") {
      resetFormData();
      setOpenAddEditModal(true);
    } else if (args === "custom_edit") {
      const data = dataGrid.current.dataView.getItem(dataGrid.current.slickGrid.getSelectedRows()[0]);
      setName(data.name);
      getSymbolsDataForEditComboBox({ id: data.id });
      setCategory(data.category);
      setEditMode(true);
      setOpenAddEditModal(true);
    } else if (args === "custom_delete") {
      setConfirmAction("delete");
    } else if (args === "set_default") {
      const currentSelectedId = dataGrid.current.slickGrid.getSelectedRows()[0];
      const currentSelectedItem = dataGrid.current.dataView.getItem(currentSelectedId);
      const data = {
        isDefault: true,
        _id: { $oid: currentSelectedItem.id },
      };
      handleEditData(data);
    } else if (args === "custom_archive") {
      setConfirmAction("archive");
    } else if (args === "custom_unarchive") {
      setConfirmAction("unarchive");
    }
  };

  const handleDropDownSelectionHandler = async (args: any) => {
    if (args.item.id === "archive_currency") {
      setView(recordStatus.ARCHIVED);
    } else if (args.item.id === "active_currency") {
      setView(recordStatus.ACTIVE);
    }
  };

  const currencyDropdownButtonsItems = useMemo(
    () => [
      {
        text: view === recordStatus.ACTIVE ? t("Show Archived Currencies") : t("Show Active Currencies"),
        id: view === recordStatus.ACTIVE ? "archive_currency" : "active_currency",
      },
    ],
    [view],
  );

  const selectingEvents = (args: any) => {
    if (!dataGrid?.current) return;

    const totalRowsSelected = args.detail.args.rows.length;
    const selectedRowIds = args.detail.args.selectedRowIds;
    setSelectedRowIds(selectedRowIds);
    const isDefaultSelected = args.detail.args.dataView.items.some(
      (item: any) => selectedRowIds.includes(item.id) && item.isDefault === true,
    );
    const isSystemGeneratedSelected = args.detail.args.dataView.items.some(
      (item: any) => selectedRowIds.includes(item.id) && (!item.createdBy || item.createdBy === "0"),
    );

    if (totalRowsSelected == 0) {
      setToolBtnState({
        create: true,
        delete: false,
        edit: false,
        archive: false,
        default: false,
        unarchive: false,
      });
    } else if (totalRowsSelected == 1) {
      setToolBtnState({
        create: true,
        delete: !isDefaultSelected && !isSystemGeneratedSelected,
        edit: true,
        archive: !isDefaultSelected,
        default: !isDefaultSelected,
        unarchive: true,
      });
    } else if (totalRowsSelected > 1) {
      setToolBtnState({
        create: true,
        delete: !isDefaultSelected && !isSystemGeneratedSelected,
        edit: false,
        archive: !isDefaultSelected,
        default: false,
        unarchive: true,
      });
    }
  };

  const getSymbolsDataForEditComboBox = async (data: any) => {
    const response = await getSymbolsData(data);
    if (response.data.status) {
      setSymbols(response.data.data ?? []);
      setSelectedSymbol(response.data.selectedSymbolData);
    }
  };

  const handleSaveData = async () => {
    if (!editMode && symbols?.length && name !== "" && category !== "") {
      const symbolsData: any = [];
      symbols.map((symbol: any) => {
        symbolsData.push(
          symbol.name === selectedSymbol.name
            ? { name: symbol.name, isSelectedSymbol: true }
            : { name: symbol.name, isSelectedSymbol: false },
        );
      });
      const data = {
        name,
        symbols: symbolsData,
        category,
        isDefault: !!isDefaultChecked,
      };
      const response = await SaveCurrencyData(data);
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("add_currency", { currency_name: data.name }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current.refresh();
        resetFormData();
        setOpenAddEditModal(false);
      }
    }
    if (editMode && symbols?.length && name !== "" && category !== "") {
      const currentSelectedItem = dataGrid.current.dataView.getItem(dataGrid.current.slickGrid.getSelectedRows()[0]);
      const data: any = {
        name,
        category,
        selectedSymbol,
        _id: { $oid: currentSelectedItem.id },
      };
      if (symbolsToAdd.length) {
        const additionalSymbol: any = [];
        symbolsToAdd.map((symbol: any) => {
          additionalSymbol.push(
            symbol.name === selectedSymbol.name
              ? { name: symbol.name, isSelectedSymbol: true }
              : { name: symbol.name, isSelectedSymbol: false },
          );
        });
        data.symbolsToAdd = additionalSymbol;
      }
      handleEditData(data);
    }
  };

  const handleDelete = async () => {
    const dataObj: any = {};
    dataObj.id = selectedRowIds;
    const response = await DeleteCurrencyData(dataObj);
    if (response.data.status) {
      if (!response.data.CannotBeDeleted && response.data.currencyName) {
        if (response.data.currencyName.length == 1) {
          dispatch(
            showToastNotification({
              content: t("delete_currency", { currency_name: response.data.currencyName[0]?.currencyName }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t("delete_currencies", {
                currency_names: response.data.currencyName.map((curr: any) => curr.currencyName).join(", "),
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }
        dataGrid.current.refresh();
        setView(recordStatus.ACTIVE);
      } else if (response.data.CannotBeDeleted) {
        dispatch(
          showToastNotification({
            content: t("One of the selected currency is already in use and hence it cannot be deleted"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const handleArchiveAction = async (action: any) => {
    const selectedIds = selectedRowIds;
    const isArchiving = action === "archive";

    const dataObj = {
      _id: selectedIds.map((id: any) => ({ $oid: id })),
      recordStatus: isArchiving ? recordStatus.ARCHIVED : recordStatus.ACTIVE,
    };

    const response = await SaveCurrencyData(dataObj);

    if (response.data.status) {
      const cannotProcess = isArchiving ? response.data.CannotBeArchived : false;

      if (!cannotProcess && response.data.currencyData) {
        const actionKey = isArchiving ? "archive" : "unarchive";
        const actionKeyPlural = isArchiving ? "archive" : "unarchive";

        if (response.data.currencyData.length === 1) {
          dispatch(
            showToastNotification({
              content: t(`${actionKey}_currency`, { currency_name: response.data.currencyData[0] }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t(`${actionKeyPlural}_currencies`, {
                currency_names: response.data.currencyData.join(", "),
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }

        if (!isArchiving) {
          setView(recordStatus.ACTIVE);
        }
      } else if (cannotProcess) {
        if (response.data.isDefault) {
          dispatch(
            showToastNotification({
              content: t("default_archive_currency", { currency_name: response.data.currencyData?.[0] ?? "" }),
              type: TOAST_TYPE_ERROR,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t("The Currency(s) cannot be archived as they are already in use."),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      }

      dataGrid.current.refresh();
    }
  };

  const handleEditData = async (data: any) => {
    if (data.selectedSymbol?.id) {
      data.selectedSymbol = { $oid: data.selectedSymbol.id };
    }
    const response = await SaveCurrencyData(data);
    if (response.data.status && editMode) {
      dispatch(
        showToastNotification({
          content: t("edit_currency", { currency_name: data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    if (response.data.status && response.data.isDefault && !editMode) {
      dispatch(
        showToastNotification({
          content: t("default_currency", { currency_name: response.data.currencyData?.[0] ?? "" }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    resetFormData();
    setOpenAddEditModal(false);
    dataGrid.current.refresh();
  };

  const addNewSymbol = (e: any) => {
    if (e.value) {
      const isStringValue = typeof e.itemData === "string";
      const text = isStringValue ? e.itemData : e.itemData.name;
      const textId = isStringValue ? undefined : e.itemData.id;
      if (text === "" || !text) return;
      const trimmedText = text.trim();
      const symbolFound = symbols.length > 0 ? symbols.find((record: any) => record.name === trimmedText) : false;
      if (symbolFound) {
        setSelectedSymbol({ name: trimmedText, id: textId });
      } else {
        setSelectedSymbol({ name: trimmedText, id: textId });
        setSymbols([...symbols, { name: trimmedText, isSelectedSymbol: false }]);

        if (editMode) {
          setSymbolsToAdd([...symbolsToAdd, { name: trimmedText, isSelectedSymbol: false }]);
        }
      }
    } else {
      setSelectedSymbol({});
    }
  };

  const getSymbolsCombobox = () => (
    <ComboBoxComponent
      ref={symbolsRef}
      allowCustom
      autoSelect
      placeholder={t("Select Symbol")}
      change={addNewSymbol}
      dataSource={symbols}
      value={selectedSymbol.name}
      fields={{ value: "name", text: "name" }}
    />
  );

  const resetFormData = () => {
    setName("");
    setSymbols([]);
    setSelectedSymbol({});
    setCategory("Prefix");
    setIsDefaultChecked(false);
    setEditMode(false);
    setSymbolsToAdd([]);
  };

  const addEditPopup = () => {
    const modalContent = (
      <div className="flex flex-col gap-5" style={{ width: "18.75rem" }}>
        <div>
          <Text className="pb-2 text-sm font-medium">{t("Currency Name")}</Text>
          <TextFieldComponent onChange={(e) => setName(e.target.value)} value={name} placeholder={t("Add Name")} />
        </div>

        <div>
          <Text className="pb-2 text-sm font-medium">{t("Symbols")}</Text>
          {getSymbolsCombobox()}
        </div>

        <div>
          <Text className="pb-4 text-sm font-medium">{t("Category")}</Text>
          <div className="flex gap-6">
            <RadioButton
              checked={category === "Prefix"}
              onClick={() => setCategory("Prefix")}
              label={t("Prefix")}
              name="category"
            />
            <RadioButton
              checked={category === "Suffix"}
              onClick={() => setCategory("Suffix")}
              label={t("Suffix")}
              name="category"
            />
          </div>
        </div>

        {!editMode && (
          <div>
            <CheckBoxComponent
              label={t("Set As Default Currency")}
              checked={isDefaultChecked}
              onChange={() => setIsDefaultChecked(!isDefaultChecked)}
            />
          </div>
        )}
      </div>
    );
    const handleCancel = () => {
      resetFormData();
      setOpenAddEditModal(false);
    };

    const footerContent = (
      <div className="flex gap-4">
        <ButtonComponent
          disabled={!(name.trim() !== "" && symbols.length > 0 && category !== "")}
          buttonType="gradientButton"
          onClick={handleSaveData}
        >
          {editMode ? t("Save") : t("Create")}
        </ButtonComponent>
        <ButtonComponent onClick={handleCancel} buttonType="borderButton">
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    return (
      <ModalStateDialogComponent
        headerTitle={editMode ? t("Edit Currency") : t("Add New Currency")}
        modelContent={modalContent}
        footerContent={footerContent}
        onCancel={handleCancel}
        isVisible={openAddEditModal}
        width="400"
      />
    );
  };

  const confirmationActions: any = {
    archive: {
      title: t("Archive Currency"),
      message: t("Are you sure you want to archive the selected currency(s)?"),
      handler: () => handleArchiveAction("archive"),
      confirmText: t("Confirm"),
    },
    unarchive: {
      title: t("Unarchive Currency"),
      message: t("Are you sure you want to unarchive the selected currency(s)?"),
      handler: () => handleArchiveAction("unarchive"),
      confirmText: t("Confirm"),
    },
    delete: {
      title: t("Delete Currency"),
      message: t("Are you sure you want to delete the selected currency(s)?"),
      handler: handleDelete,
      confirmText: t("Delete"),
    },
  };

  const confirmationPopup = () => {
    if (!confirmAction) return null;

    const action = confirmationActions[confirmAction];

    return (
      <ModalStateDialogComponent
        headerTitle={action.title}
        modelContent={action.message}
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={() => {
                action.handler();
                setConfirmAction(null);
              }}
              buttonType="gradientButton"
            >
              <Text>{action.confirmText}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={() => setConfirmAction(null)} buttonType="borderButton">
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={() => setConfirmAction(null)}
        isVisible={!!confirmAction}
        width={confirmAction === "delete" ? undefined : "440"}
      />
    );
  };

  const getDataGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      columnDefinitions={currencyColumnDefinitions.current as any}
      remoteDataSource={getCurrencyDataUrl}
      query={query}
      infiniteScroll
      enableRowResize
      enableFilteringOption
      enableColumnChooser
      enableGroupingOption
      toolbarClickHandler={toolbarClickHandler}
      toolbar={toolbarOptions}
      onSelectedRowIdsChanged={selectingEvents}
      enableSortingOption
      excelExport
    />
  );

  return (
    <>
      <GridPageLayout customHeader={<CurrenciesHeader isActive={view === recordStatus.ACTIVE} />}>
        {getDataGrid()}
      </GridPageLayout>
      {openAddEditModal && view === recordStatus.ACTIVE ? addEditPopup() : null}
      {confirmationPopup()}
    </>
  );
}

export default Currencies;
